import React, { FC, useMemo, useState, useRef, useEffect, useCallback } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import clsx from 'clsx'
import { ListEmailDomainsEnum } from '@sparling/financial-common'
import { FormControl, FormHelperText, Grid, InputLabel, OutlinedInput, Theme } from '@mui/material'
import { makeStyles } from '@mui/styles'
import capitalizeFirstLetter from '../../utils/capitalizeFirstLetter'
import { errorMessage as errorText } from '../../constants/errorMessages'
import { Colors } from '../../theme'

const useStyles = makeStyles<Theme, InputFieldProps>({
  inputContainer: {
    marginBottom: ({ maximumHeight }) => (maximumHeight ? 0 : 20),
    width: ({ fullWidth, width }) => (fullWidth ? '100%' : width),
    maxHeight: ({ maximumHeight }) => maximumHeight || 63,
  },
  errorMsg: {
    height: 'auto',
    maxHeight: 'none',
    marginBottom: 0,
  },
  inputContainerNoLabel: {
    marginBottom: 0,
    maxHeight: ({ maximumHeight }) => maximumHeight || '40px !important',
  },
  label: {
    fontSize: '18px',
    marginBottom: '-3px',
    color: '#FFFFFF',
    opacity: '60%',
  },
  formControl: {
    width: ({ fullWidth, width }) => (fullWidth ? '100%' : width),
    height: (multiline) => (multiline ? 'auto' : 40),
    boxSizing: 'border-box',
    '& .MuiInputBase-root': {
      fontSize: '16px !important',
    },
    '& .MuiOutlinedInput-input': {
      paddingTop: ({ withoutPadding }) => (withoutPadding ? 0 : 20),
      paddingBottom: ({ withoutPadding }) => (withoutPadding ? 0 : 20),
      boxSizing: 'border-box',
      '&.Mui-disabled': {
        color: '#FFFFFF',
        opacity: 1,
        '-webkit-text-fill-color': '#FFFFFF',
      },
    },
    '& .MuiOutlinedInput-input:-webkit-autofill': {
      '-webkit-box-shadow': 'none',
    },
    '& :-webkit-autofill': {
      transition: 'background-color 5000s ease-in-out 0s',
    },
    '& .MuiOutlinedInput-root.Mui-disabled': {
      '& fieldset': {
        borderColor: '#FFFFFF',
        opacity: 0.4,
      },
      color: '#FFFFFF',
      opacity: 1,
    },
  },
  companyEmailText: {
    color: Colors.yellow,
  },
  isShowCompanyTextMessage: {
    marginBottom: 40,
  },
})

type InputType = {
  inputType:
    | 'button'
    | 'checkbox'
    | 'color'
    | 'date'
    | 'datetime-local'
    | 'email'
    | 'file'
    | 'hidden'
    | 'image'
    | 'month'
    | 'number'
    | 'password'
    | 'radio'
    | 'range'
    | 'reset'
    | 'search'
    | 'submit'
    | 'tel'
    | 'text'
    | 'time'
    | 'url'
    | 'week'
}

interface InputFieldProps extends InputType {
  labelName?: string
  inputName: string
  placeholder?: string
  errorMessageText?: string | undefined
  isRequired?: boolean
  isCompanyEmail?: boolean
  min?: number
  rows?: number
  multiline?: boolean
  maximumHeight?: string | number
  defaultValue?: string | number
  onBlur?: () => void
  isCustomError?: boolean
  errorTextCustomError?: string
  inputValue?: string
  disabled?: boolean
  width?: string | number
  fullWidth?: boolean
  withoutPadding?: boolean
}

const smartSearch = (domain: string, text: string): boolean => {
  const result = text.replace(/^[A-Za-z0-9._%+-]+@/, '')
  return result === domain
}

const popularEmailDomainsValidator = (value: string) => {
  if (!value) return true
  if (/@/.test(value)) {
    // eslint-disable-next-line guard-for-in,no-restricted-syntax
    for (const domain in ListEmailDomainsEnum) {
      if (smartSearch(ListEmailDomainsEnum[domain as keyof typeof ListEmailDomainsEnum], value)) {
        return true
      }
    }
    return false
  }
  return true
}

export const InputField: FC<InputFieldProps> = ({
  labelName,
  inputName,
  inputType,
  placeholder,
  errorMessageText,
  isCompanyEmail,
  min,
  rows,
  multiline,
  maximumHeight,
  defaultValue = '',
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onBlur = () => {},
  isCustomError,
  errorTextCustomError,
  isRequired,
  disabled = false,
  width = 300,
  fullWidth,
  withoutPadding,
}) => {
  const {
    label,
    inputContainer,
    inputContainerNoLabel,
    formControl,
    companyEmailText,
    isShowCompanyTextMessage,
    errorMsg,
  } = useStyles({
    labelName,
    inputName,
    inputType,
    placeholder,
    errorMessageText,
    isCompanyEmail,
    min,
    rows,
    multiline,
    maximumHeight,
    defaultValue,
    onBlur,
    isCustomError,
    errorTextCustomError,
    isRequired,
    width,
    fullWidth,
    withoutPadding,
  })

  const [isCompanyEmailError, setIsCompanyEmailError] = useState<boolean>(false)
  const inputEl = useRef<HTMLInputElement>(null)
  const {
    register,
    control,
    setValue,
    formState: { errors },
    // eslint-disable-next-line
  } = useFormContext()
  const error = errors[inputName]

  const errorMessage = useMemo(() => {
    const message = error?.message
    return capitalizeFirstLetter(message)
  }, [error])

  const emailValidator = useCallback(
    // eslint-disable-next-line
    ($event: any) => {
      if (isCompanyEmail) {
        const {
          target: { value },
        } = $event
        setIsCompanyEmailError(value.length ? popularEmailDomainsValidator(value) : false)
      }
    },
    [isCompanyEmail],
  )

  useEffect(() => {
    if (isCompanyEmail) {
      inputEl?.current?.addEventListener('change', ($event) => emailValidator($event))
    }
    inputEl?.current?.addEventListener('blur', onBlur)
    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      if (isCompanyEmail) inputEl?.current?.removeEventListener('change', ($event) => emailValidator($event))
      // eslint-disable-next-line react-hooks/exhaustive-deps
      inputEl?.current?.removeEventListener('blur', onBlur)
    }
    // eslint-disable-next-line
  }, [emailValidator, isCompanyEmail])

  useEffect(() => {
    inputEl?.current?.addEventListener('blur', onBlur)
    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      inputEl?.current?.removeEventListener('blur', onBlur)
    }
  }, [onBlur])

  const handleChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setValue(inputName, event.target.value)
    // eslint-disable-next-line
  }, [])

  return (
    <Grid
      container
      item
      className={clsx(
        !labelName ? inputContainerNoLabel : '',
        inputContainer,
        errorMessageText && errorMsg,
        !error && isCompanyEmailError && isCompanyEmail ? isShowCompanyTextMessage : undefined,
      )}
      direction={'column'}
      wrap={'nowrap'}
    >
      {labelName && (
        <Grid item>
          <InputLabel shrink htmlFor={inputName} className={label}>
            {labelName}
          </InputLabel>
        </Grid>
      )}

      <Grid item>
        <FormControl size={'small'} className={formControl}>
          <Controller
            rules={{ required: isRequired }}
            control={control}
            name={inputName}
            defaultValue={defaultValue || ''}
            render={() => (
              <OutlinedInput
                disabled={disabled}
                {...register(inputName, {
                  onChange: handleChange,
                })}
                autoComplete={'new-password'}
                inputProps={{
                  min: min || inputType === 'number' ? 0 : '',
                }}
                error={!!error || isCustomError || !!errorMessageText}
                multiline={multiline}
                placeholder={placeholder}
                inputRef={inputEl}
                type={inputType}
                defaultValue={defaultValue || ''}
                id={inputName}
                color={'primary'}
                rows={rows}
              />
            )}
          />
        </FormControl>
      </Grid>
      <Grid item>
        {(error || errorMessageText) && (
          <FormHelperText id={`component-error-text-${inputName}`}>{errorMessageText || errorMessage}</FormHelperText>
        )}
        {!error && isCompanyEmailError && isCompanyEmail && (
          <FormHelperText className={companyEmailText} id={`component-error-text-${inputName}`}>
            {errorText.RECOMMENDATION_CORPORATE_MAIL}
          </FormHelperText>
        )}
        {!error && isCustomError && (
          <FormHelperText id={`component-error-text-${inputName}`}>{errorTextCustomError}</FormHelperText>
        )}
      </Grid>
    </Grid>
  )
}
